import { useEffect } from "react";
import { datadogRum, DefaultPrivacyLevel } from "@datadog/browser-rum";
import { Environment } from "./AppConfigSlice";
import { useAppSelector } from "redux/hooks";

// Use OneTrust essential cookie category since DataDog is used for
// error reporting which is deemed as essential app functionalities.
const TELEMETRY_COOKIE_GROUP = "C0001";

/**
 * Telemetry class is to support DataDog integration with OneTrust.
 *
 * It's adapted from PHAF Telemetry class:
 *   https://github.com/verily-src/verily1/blob/main/phaf/mfe/telemetry/src/telemetry.ts
 *
 * Note that since we use essential cookie category for DataDog, OneTrust will always
 * return that user accepted these cookies. This means that OneTrust integration is not
 * needed for now. But, we still want to keep this code in case there is a change to
 * classify DataDog cookies as analytics cookies.
 */
class Telemetry {
  private isRunning = false;
  private isInitialized = false;
  private environment: Environment = "local";

  public setEnvironment(env: Environment) {
    this.environment = env;
  }

  public logError(error: unknown) {
    if (this.isRunning) {
      datadogRum.addError(error);
    }
  }

  /**
   * Checks if the user has granted consent for the analytics cookie group.
   */
  private isOneTrustTelemetryConsentGranted(): boolean {
    // @ts-ignore
    const { OnetrustActiveGroups } = window;

    if (OnetrustActiveGroups) {
      return OnetrustActiveGroups.includes(TELEMETRY_COOKIE_GROUP);
    }
    return false;
  }

  /**
   * Starts or stops data collection based on the user consent
   * and the running status of datadog.
   */
  public handleOneTrustContentChange(): void {
    const hasPermission = this.isOneTrustTelemetryConsentGranted();

    // If telemetry is not running and user has granted consent, start it.
    if (hasPermission && !this.isRunning) {
      if (this.isInitialized) {
        this.reactivateDataCollection();
      } else {
        this.initializeDatadog();
      }
    } else if (!hasPermission && this.isRunning) {
      // If telemetry is running and user has not granted consent, tear it down.
      this.stopDataCollection();
    }
  }

  /**
   * Initializes the datadog RUM. See reference doc:
   *   https://docs.datadoghq.com/real_user_monitoring/browser/setup/client?tab=rum
   */
  private initializeDatadog() {
    datadogRum.init({
      applicationId: "59a67e44-7c2a-480d-9359-6fa817d81cc2",
      clientToken: "pube6acee15a8230602f8b554e03d18680e",
      site: "us5.datadoghq.com",
      service: "numetric-watch-portal",
      env: this.environment,
      version: process.env.REACT_APP_VERSION,
      sessionSampleRate: 100,
      // Disable session replay and user interaction tracking to lower the likelihood
      // of collecting sensitive data.
      sessionReplaySampleRate: 0,
      trackUserInteractions: false,
      defaultPrivacyLevel: DefaultPrivacyLevel.MASK,
      startSessionReplayRecordingManually: false,
      trackingConsent: "granted",
    });

    this.isRunning = true;
    this.isInitialized = true;
  }

  /**
   * Reactivates collecting data after it has been stopped by removing consent
   */
  private reactivateDataCollection() {
    datadogRum.setTrackingConsent("granted");
    this.isRunning = true;
  }

  /**
   * Stops collecting data when user consent is removed
   */
  private stopDataCollection() {
    datadogRum.setTrackingConsent("not-granted");
    this.isRunning = false;
  }
}

export const userNotAuthenticated = "User is not authenticated";
export const lookerReturnsInvalidResults = "Looker returns invalid results";

export const telemetry = new Telemetry();

const TelemetryController: React.FC = () => {
  const appConfig = useAppSelector((state) => state.appConfig);

  useEffect(() => {
    telemetry.setEnvironment(appConfig.environment);

    // Handle initial state. This function is idempotent, the result will only change if the consent changes.
    telemetry.handleOneTrustContentChange();

    const handleOneTrustChange = () => {
      telemetry.handleOneTrustContentChange();
    };

    window.addEventListener("OneTrustGroupsUpdated", handleOneTrustChange);

    return () => {
      window.removeEventListener("OneTrustGroupsUpdated", handleOneTrustChange);
    };
  }, [appConfig.environment]);

  return <></>;
};

export default TelemetryController;
